/**
 * Load Apple Pay SDK from Apple's CDN
 * Returns a promise that resolves when SDK is loaded or if it's already available
 * @returns {Promise<void>}
 */
export function loadApplePaySDK() {
  return new Promise((resolve) => {
    if (window.ApplePaySession) {
      return resolve();
    }

    const script = document.createElement('script');
    script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    script.onload = () => resolve();
    script.onerror = () => {
      console.warn('Failed to load Apple Pay SDK from CDN');
      resolve();
    };

    document.head.appendChild(script);
  });
}

/**
 * Check if Apple Pay is available on this device/browser
 * Loads the SDK on demand when checking availability
 * @returns {boolean} True if Apple Pay can be used
 */
export async function checkApplePayAvailability() {
  // 1. Basic User Agent Check to filter out non-Apple platforms early
  // TODO: This is technically wrong, however, because Apple Pay is also supported on Windows, Android, etc.
  const userAgent = navigator.userAgent.toLowerCase();
  const isAppleOS = userAgent.includes('macintosh') || userAgent.includes('iphone') || userAgent.includes('ipad');

  if (!isAppleOS) {
    return false;
  }

  // Load SDK if interface not already available
  await loadApplePaySDK();

  // 2. Check if Apple Pay JS API is available in the browser
  if (!window.ApplePaySession) {
    return false;
  }

  // 3. Check if the hardware capability check method exists
  if (typeof window.ApplePaySession.canMakePayments !== 'function') {
    return false;
  }

  try {
    // 4. Query the native hardware check
    // TODO: Implement full availability check via ApplePaySession.applePayCapabilities(),
    // but this requires the per-vendor merchant identifier. Just use hardware check for now.
    const canMakePayments = window.ApplePaySession.canMakePayments();
    return !!canMakePayments;
  } catch (error) {
    console.error('Apple Pay availability check failed:', error);
    return false;
  }
}
